import React from 'react';
import styled from 'styled-components';
import { useModalContext } from '../App';

interface LearnMoreButtonProps {
  className?: string;
  serviceName?: string;
  /** Full modal header text; falls back to the "Learn More About …" template. */
  modalTitle?: string;
  buttonText?: string;
  isPrimary?: boolean;
  hasArrow?: boolean;
}

// Styled components should use $prefixed props to avoid DOM warnings
const Button = styled.button<{ $isPrimary?: boolean; $hasArrow?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background-color: ${props => props.$isPrimary ? 'var(--primary-color)' : 'transparent'};
  color: ${props => props.$isPrimary ? 'white' : 'var(--primary-color)'};
  border: 1px solid ${props => props.$isPrimary ? 'var(--primary-color)' : 'var(--primary-color)'};
  padding: 0.75rem 1.75rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${props => props.$isPrimary ? '#e05e00' : 'rgba(255, 107, 0, 0.1)'};
    ${props => !props.$isPrimary && 'color: var(--primary-color);'}
  }
  
  svg {
    transition: transform 0.3s ease;
  }
  
  &:hover svg {
    transform: translateX(4px);
  }
`;

// Arrow icon component
const ArrowIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M13.5 4.5L21 12L13.5 19.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M3 12H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

/**
 * A reusable button component for triggering the Learn More modal
 * This component delegates the modal handling to the ModalContext
 */
const LearnMoreButton: React.FC<LearnMoreButtonProps> = ({ 
  className, 
  serviceName, 
  modalTitle,
  buttonText = "Learn More", 
  isPrimary = false,
  hasArrow = true
}) => {
  const { openLearnMoreModal } = useModalContext();
  
  const handleClick = () => {
    openLearnMoreModal(serviceName, modalTitle);
  };
  
  return (
    <Button 
      className={className} 
      onClick={handleClick} 
      $isPrimary={isPrimary}
      $hasArrow={hasArrow}
    >
      {buttonText}
      {hasArrow && <ArrowIcon />}
    </Button>
  );
};

export default LearnMoreButton; 